import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ShoppingCart, Package, CheckCircle } from '@phosphor-icons/react';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { theme } = useTheme();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState(0);
    const [showReservation, setShowReservation] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [orderSuccess, setOrderSuccess] = useState(false);
    const [formData, setFormData] = useState({
        nom: '',
        prenom: '',
        telephone: '',
        email: ''
    });

    useEffect(() => {
        fetchProduct();
    }, [id]);

    const fetchProduct = async () => {
        try {
            const response = await axios.get(`${API}/products/${id}`);
            setProduct(response.data);
        } catch (error) {
            console.error('Error fetching product:', error);
            toast.error('Produit non trouvé');
            navigate('/materiel');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleReservation = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await axios.post(`${API}/orders/products`, {
                product_id: product.id,
                client: formData
            });
            setOrderSuccess(true);
        } catch (error) {
            console.error('Error creating order:', error);
            toast.error('Erreur lors de la réservation');
        } finally {
            setSubmitting(false);
        }
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('fr-SN', { style: 'currency', currency: 'XOF' }).format(price);
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-mmd-gold"></div>
            </div>
        );
    }

    if (!product) return null;

    return (
        <div data-testid="product-detail-page" className={`min-h-screen ${theme === 'dark' ? 'bg-mmd-dark-bg' : 'bg-mmd-light-bg'}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Back Button */}
                <button
                    onClick={() => navigate('/materiel')}
                    data-testid="back-button"
                    className={`inline-flex items-center gap-2 mb-8 text-sm font-medium transition-colors ${
                        theme === 'dark' ? 'text-mmd-silver hover:text-mmd-gold' : 'text-mmd-navy/70 hover:text-mmd-navy'
                    }`}
                >
                    <ArrowLeft size={18} />
                    Retour au catalogue
                </button>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
                    {/* Image Gallery */}
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                    >
                        <div className={`rounded-2xl overflow-hidden ${theme === 'dark' ? 'bg-mmd-surface-dark' : 'bg-white'}`}>
                            <div className="aspect-square">
                                <img
                                    src={product.images?.[selectedImage] || 'https://via.placeholder.com/600x600?text=No+Image'}
                                    alt={product.nom}
                                    className="w-full h-full object-cover"
                                    data-testid="main-product-image"
                                />
                            </div>
                        </div>
                        {product.images?.length > 1 && (
                            <div className="flex gap-3 mt-4 overflow-x-auto pb-2">
                                {product.images.map((img, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => setSelectedImage(idx)}
                                        className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                                            selectedImage === idx
                                                ? theme === 'dark' ? 'border-mmd-gold' : 'border-mmd-navy'
                                                : 'border-transparent'
                                        }`}
                                    >
                                        <img src={img} alt="" className="w-full h-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </motion.div>

                    {/* Product Info */}
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="flex flex-col"
                    >
                        <p className={`text-sm font-medium uppercase tracking-wide mb-2 ${theme === 'dark' ? 'text-mmd-gold' : 'text-mmd-navy'}`}>
                            {product.marque}
                        </p>
                        <h1 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight mb-4" data-testid="product-name">
                            {product.nom}
                        </h1>
                        
                        <div className="flex items-center gap-4 mb-6">
                            <span className={`text-2xl font-bold ${theme === 'dark' ? 'text-mmd-gold' : 'text-mmd-navy'}`} data-testid="product-price">
                                {formatPrice(product.prix)}
                            </span>
                            <span className={`text-sm px-3 py-1 rounded-full ${
                                product.stock > 0 
                                    ? 'bg-green-500/20 text-green-500' 
                                    : 'bg-red-500/20 text-red-500'
                            }`} data-testid="product-stock">
                                {product.stock > 0 ? `${product.stock} en stock` : 'Rupture de stock'}
                            </span>
                        </div>

                        <div className={`prose prose-sm max-w-none mb-8 ${theme === 'dark' ? 'prose-invert' : ''}`}>
                            <p className={`leading-relaxed ${theme === 'dark' ? 'text-mmd-silver/80' : 'text-mmd-navy/70'}`} data-testid="product-description">
                                {product.description}
                            </p>
                        </div>

                        <div className="mt-auto">
                            <Button
                                onClick={() => setShowReservation(true)}
                                disabled={product.stock === 0}
                                data-testid="reserve-button"
                                className={`w-full sm:w-auto px-8 py-6 text-lg font-medium ${
                                    theme === 'dark'
                                        ? 'bg-mmd-gold text-mmd-dark-bg hover:bg-mmd-gold-hover'
                                        : 'bg-mmd-navy text-white hover:bg-mmd-navy-hover'
                                }`}
                            >
                                <ShoppingCart size={20} className="mr-2" />
                                Réserver
                            </Button>
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Reservation Dialog */}
            <Dialog open={showReservation} onOpenChange={setShowReservation}>
                <DialogContent className="sm:max-w-md">
                    {orderSuccess ? (
                        <div className="text-center py-8">
                            <CheckCircle size={64} className="mx-auto mb-4 text-green-500" weight="fill" />
                            <h3 className="font-heading text-xl font-medium mb-2">Commande enregistrée !</h3>
                            <p className="text-muted-foreground mb-6">
                                Votre réservation a été bien enregistrée. Nous vous contacterons prochainement.
                            </p>
                            <Button onClick={() => { setShowReservation(false); setOrderSuccess(false); }}>
                                Fermer
                            </Button>
                        </div>
                    ) : (
                        <>
                            <DialogHeader>
                                <DialogTitle>Réserver {product.nom}</DialogTitle>
                                <DialogDescription>
                                    Remplissez vos coordonnées pour réserver ce produit.
                                </DialogDescription>
                            </DialogHeader>
                            <form onSubmit={handleReservation} className="space-y-4 mt-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label htmlFor="nom">Nom</Label>
                                        <Input
                                            id="nom"
                                            name="nom"
                                            value={formData.nom}
                                            onChange={handleInputChange}
                                            required
                                            data-testid="reservation-nom"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="prenom">Prénom</Label>
                                        <Input
                                            id="prenom"
                                            name="prenom"
                                            value={formData.prenom}
                                            onChange={handleInputChange}
                                            required
                                            data-testid="reservation-prenom"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label htmlFor="telephone">Téléphone</Label>
                                    <Input
                                        id="telephone"
                                        name="telephone"
                                        type="tel"
                                        value={formData.telephone}
                                        onChange={handleInputChange}
                                        required
                                        data-testid="reservation-telephone"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="email">Email</Label>
                                    <Input
                                        id="email"
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        required
                                        data-testid="reservation-email"
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    className="w-full"
                                    disabled={submitting}
                                    data-testid="submit-reservation"
                                >
                                    {submitting ? 'Envoi en cours...' : 'Confirmer la réservation'}
                                </Button>
                            </form>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
